<script>
import $ from 'domtastic';
import sumBy from 'lodash/sumBy';
import get from 'lodash/get';
import vueMixins from 'viralstyle-ui/src/vueMixins';

import BuyerInput from './BuyerInput';
import IconChevronDown from '../icons/IconChevronDown';
import IconCart from '../icons/IconCart';
import CheckoutStep from './CheckoutStep';
import { mapState, mapGetters } from 'vuex';

export default {
  components: {
    BuyerInput,
    IconChevronDown,
    IconCart,
    CheckoutStep,
  },
  mixins: [vueMixins],
  props: {
    cart: Object,
    search: String,
    marketplaceCategories: Array,
    userLoggedIn: Boolean,
    cartRoute: String,
    checkoutRoute: String,
    collapsed: Boolean,
    fixed: Boolean,
    hideFixedHeader: Boolean,
    isWhitelabel: [String, Boolean],
    showHomeLink: [String, Boolean],
  },
  computed: {
    ...mapState({
      processing: state => state.processing,
    }),
    ...mapGetters(['cartQuantity']),
  },
  data: () => ({
    showShopByMenu: false,
  }),
  created() {
    $(window).on('scroll', this.$_adjustMenuHeight);
    $(window).on('resize', this.$_adjustMenuHeight);
  },
  beforeDestroy() {
    $(window).off('scroll', this.$_adjustMenuHeight);
    $(window).off('resize', this.$_adjustMenuHeight);

    this.$_unbindCloseShopByMenu();
  },
  methods: {
    async $_adjustMenuHeight() {
      await this.$nextTick();

      if (this.showShopByMenu) {
        let headerEl = this.$el.querySelector('.header-bottom__shop-by-menu');

        headerEl.style.height = 'auto';

        if (this.elementOutOfViewport(headerEl).bottom) {
          headerEl.style.height = `${(window.innerHeight || document.documentElement.clientHeight) - headerEl.getBoundingClientRect().top}px`;
        }
      }
    },
    $_unbindCloseShopByMenu() {
      $(window).off('click', this.$_closeShopByMenu);
    },
    $_closeShopByMenu() {
      this.showShopByMenu = false;

      this.$_unbindCloseShopByMenu();
    },
    toggleShopMenu(e) {
      e.stopPropagation();

      this.showShopByMenu = !this.showShopByMenu;

      if (this.showShopByMenu) {
        this.$_adjustMenuHeight();

        $(window).on('click', this.$_closeShopByMenu);
      } else {
        this.$_unbindCloseShopByMenu();
      }
    },
    marketplaceRoute(category) {
      let route = {
        name: 'marketplace',
        params: {
          page: '1',
          nav: true,
        },
      };

      if (category.value) {
        route.query = {
          category: category.value,
        };
      }

      return route;
    },
  },
};
</script>

<template lang='pug'>
.bg-white.border-b.border-border.header-bottom.z-100(
  :class='{ "header-bottom--collapsed": collapsed, "fixed w-full top-0": fixed, "hidden": fixed && hideFixedHeader, "invisible": !fixed && !hideFixedHeader, "relative": !fixed }',
)
  .container.flex.items-center.justify-between.mx-auto.h-full.flex-wrap.px-1

    //- search input
    .w-1-3.xl_w-1-4.header-bottom__search
      buyer-input(
        placeholder='Search',
        type='search',
        :value='search',
        @input='$emit("search-input", $event)',
        @search='$emit("perform-search", $event)',
      )

    //- logo slot
    slot

    //- right links
    .flex.items-center.w-1-3.justify-end(
      :class='isWhitelabel && showHomeLink ? "xl_w-1-3" : "xl_w-1-4"'
    )

      //- shop by link
      template(v-if='isWhitelabel')
        router-link.text-xl.cursor-pointer.mr-1.lg_mr-1_5.xl_mr-2.select-none(
          v-if='showHomeLink',
          tabindex='0',
          tag='div',
          :to='{ name: "home" }',
        ) Home
        router-link.text-xl.cursor-pointer.mr-1.lg_mr-1_5.xl_mr-2.select-none(
          tabindex='0',
          tag='div',
          :to='{ name: "trackOrder" }',
        ) Track Order
        router-link.text-xl.cursor-pointer.select-none(
          tabindex='0',
          tag='div',
          :class='cartQuantity ? "mr-1 lg_mr-1_5 xl_mr-2" : null',
          :to='{ name: "contact" }',
        ) Contact

      template(v-else)
        .text-xl.mr-1.lg_mr-1_5.xl_mr-2.relative.cursor-pointer.select-none(
          v-if='!isWhitelabel && marketplaceCategories.length',
          ref='shopByMenu',
          tabindex='0',
          @click='toggleShopMenu',
          @keyup.enter='toggleShopMenu',
        ) Shop By
          icon-chevron-down.ml-_5.text-brand
          .header-bottom__shop-by-menu.absolute.left-0.rounded.bg-white.z-10.overflow-auto(v-show='showShopByMenu')
            router-link.text-lg(
              v-for='category in marketplaceCategories',
              tabindex='0',
              tag='div',
              :key='category.slug',
              :to='marketplaceRoute(category)',
            )
              a.transition-250.transition-color.hover_text-brand.cursor-pointer.block.px-1.py-_25 {{category.text}}

        //- start selling link
        router-link.text-xl.cursor-pointer(
          tabindex='0',
          tag='div',
          :class='cartQuantity ? "mr-1 lg_mr-1_5 xl_mr-2" : null',
          :to='{ name: "openShop" }',
        ) Start Selling

      router-link(
        v-if='cartQuantity',
        tag='div',
        :to='{ name: "cart", params: { id: cart.id } }',
      )
        icon-cart.text-brand(:qty='cartQuantity')
</template>

<style>
@import '~import/index';

.header-bottom {
  $self: &;

  &__shop-by-menu {
    top: calc(100% + .1rem);
    box-shadow: 0 9px 22px rgba(0, 0, 0, 0.16);
    width: 215px;

    > div {
      &:first-child > a {
        padding-top: .75rem;
      }

      &:last-child > a {
        padding-bottom: .75rem;
      }
    }
  }

  >.container {
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
    transition: padding 250ms;
  }

  @include mq(mf-md) {
    &--collapsed {
      >.container {
        padding-top: .5rem;
        padding-bottom: .5rem;
      }
    }
  }
}
</style>